import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "@/lib/config";
import {
  appOrigin,
  buildAppPath,
  buildPublicQuestionPath,
} from "@/lib/app-route";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { Check, CheckCircle, Link as LinkIcon } from "@/components/ui/icons";
import { formatRelativeTime } from "@/lib/time-utils";
import { setPendingAutoJoin } from "@/lib/pending-auto-join";
import { trackPublicPageViewed } from "@/lib/analytics-events";
import {
  fetchPublicQuestion,
  type PublicQuestionResponse,
  type PublicWorkspaceMetadata,
} from "@ano/shared/api/public-workspaces";
import { PublicReplyComposer } from "./PublicReplyComposer";
import { AnonymousHeader } from "./PublicWorkspaceAnonymousView.parts";

/** How long before a stalled question fetch falls back to the unavailable state. */
const QUESTION_FETCH_TIMEOUT_MS = 8000;
/** Live-poll cadence — re-fetch to surface new answers without a reload. */
const QUESTION_POLL_INTERVAL_MS = 12000;

type LoadState = "loading" | "loaded" | "unavailable";

interface PublicQuestionViewProps {
  /** Already-fetched public metadata (owner enabled `anonymous_view`). */
  meta: PublicWorkspaceMetadata;
  /** Channel name from the `…/c/<channel>/m/<id>` deep link. */
  channelName: string;
  /** Anchored message id — the single shared question. */
  messageId: string;
  /** Start the WorkOS sign-in flow (returnTo is the current URL). */
  onSignIn: () => void;
  /** Start the WorkOS sign-up flow. */
  onSignUp: () => void;
  /** One-click GitHub sign-up (deep-links to the WorkOS GitHub connection). */
  onContinueWithGithub: () => void;
}

/**
 * Logged-OUT single-question surface (the `…/c/<channel>/m/<id>` anchor link).
 *
 * Sibling of `PublicWorkspaceAnonymousView`: rendered by `AuthGate` BEFORE the
 * sign-in wall when an unauthenticated visitor lands on a shared-question deep
 * link of a workspace whose owner enabled `anonymous_view`. Shows ONE question
 * + its answers (server-redacted, top-level only, no emails) so a stranger who
 * followed an X/Reddit unfurl reads the discussion and converts by answering.
 *
 * REST-ONLY by construction: renders OUTSIDE `<ZeroProvider>` with no auth
 * token, so it talks only to the unauthenticated `/c/:channel/m/:id` endpoint.
 * It never touches Zero, mutators, or `useApiClient`.
 */
export function PublicQuestionView({
  meta,
  channelName,
  messageId,
  onSignIn,
  onSignUp,
  onContinueWithGithub,
}: PublicQuestionViewProps) {
  const [data, setData] = useState<PublicQuestionResponse | null>(null);
  const [status, setStatus] = useState<LoadState>("loading");
  const [copied, setCopied] = useState(false);

  // Initial load: bounded by a timeout so a stalled network degrades to the
  // graceful "unavailable" state instead of an infinite spinner.
  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (!cancelled) setStatus("unavailable");
    }, QUESTION_FETCH_TIMEOUT_MS);
    void fetchPublicQuestion({
      apiBaseUrl: API_URL,
      slug: meta.slug,
      channelName,
      messageId,
    })
      .then((res) => {
        window.clearTimeout(timeout);
        if (cancelled) return;
        setData(res);
        setStatus(res ? "loaded" : "unavailable");
      })
      .catch(() => {
        window.clearTimeout(timeout);
        if (cancelled) return;
        setStatus("unavailable");
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [meta.slug, channelName, messageId]);

  // Live polling (Phase 4): once the question is loaded, re-fetch every ~12s to
  // surface new answers. Paused while the tab is hidden (visibilitychange) so a
  // backgrounded tab doesn't poll. A poll that 404s (question removed) flips the
  // surface to "unavailable" rather than silently freezing on stale content.
  useEffect(() => {
    if (status !== "loaded") return;
    let cancelled = false;
    let timer: number | undefined;

    const poll = () => {
      void fetchPublicQuestion({
        apiBaseUrl: API_URL,
        slug: meta.slug,
        channelName,
        messageId,
      })
        .then((res) => {
          if (cancelled) return;
          if (res) setData(res);
          else setStatus("unavailable");
        })
        .catch(() => {
          /* transient blip — keep the current content, try again next tick */
        });
    };

    const schedule = () => {
      if (document.visibilityState === "hidden") return;
      timer = window.setInterval(poll, QUESTION_POLL_INTERVAL_MS);
    };
    const onVisibility = () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
        timer = undefined;
      }
      if (document.visibilityState === "visible") {
        poll(); // catch up immediately on return, then resume the interval
        schedule();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [status, meta.slug, channelName, messageId]);

  // Funnel top: a logged-out visitor opened a single shared question. Fired once
  // per (slug, messageId) landing, with the question-deeplink flag set so this
  // surface is distinguishable from the channel-level deep link.
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackPublicPageViewed({
      slug: meta.slug,
      workspaceId: meta.workspace_id,
      isChannelDeeplink: true,
      isQuestionDeeplink: true,
      channelName,
      memberCount: meta.member_count,
      channelCount: meta.channel_count,
      entryMode: meta.entry_mode,
    });
  }, [
    meta.slug,
    meta.workspace_id,
    meta.member_count,
    meta.channel_count,
    meta.entry_mode,
    channelName,
  ]);

  const handleGithub = useCallback(() => {
    if (meta.public_join_policy === "auto") setPendingAutoJoin(meta.slug);
    onContinueWithGithub();
  }, [meta.public_join_policy, meta.slug, onContinueWithGithub]);

  const handleCopyShareLink = useCallback(() => {
    const shareUrl = `${appOrigin(window.location, { shareable: true })}${buildPublicQuestionPath(
      meta.slug,
      channelName,
      messageId,
    )}`;
    void navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [meta.slug, channelName, messageId]);

  const onCta = (action: "signup" | "signin", _placement: "header") => {
    if (action === "signup") {
      if (meta.public_join_policy === "auto") setPendingAutoJoin(meta.slug);
      onSignUp();
    } else onSignIn();
  };

  return (
    <div className="flex h-screen flex-col bg-background">
      <AnonymousHeader meta={meta} onCta={onCta} />
      <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        {status === "loading" && (
          <div className="flex justify-center py-16">
            <LoadingIcon size={24} className="text-muted-foreground" />
          </div>
        )}

        {status === "unavailable" && (
          <QuestionUnavailable slug={meta.slug} channelName={channelName} />
        )}

        {status === "loaded" && data && (
          <QuestionContent
            data={data}
            meta={meta}
            channelName={channelName}
            messageId={messageId}
            copied={copied}
            onCopyShareLink={handleCopyShareLink}
            onContinueWithGithub={handleGithub}
          />
        )}

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Powered by Ano
        </p>
      </main>
    </div>
  );
}

/** Graceful 404 — the question isn't public/available; offer breadcrumbs back
 *  to the channel and the community rather than dead-ending at the sign-in wall. */
function QuestionUnavailable({
  slug,
  channelName,
}: {
  slug: string;
  channelName: string;
}) {
  return (
    <section className="mt-8 space-y-4 text-center">
      <h1 className="text-xl font-semibold tracking-tight">
        This question isn't available
      </h1>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">
        It may have been removed, or it isn't part of a public channel. You can
        still explore the rest of the community.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
        <Button variant="outline" size="sm" asChild>
          <a href={buildAppPath(slug, channelName)}>
            <span className="text-muted-foreground">#</span>
            {channelName}
          </a>
        </Button>
        <Button variant="ghost" size="sm" asChild>
          <a href={buildAppPath(slug)}>Back to community</a>
        </Button>
      </div>
    </section>
  );
}

/** The loaded question + answers + reply composer body. */
function QuestionContent({
  data,
  meta,
  channelName,
  messageId,
  copied,
  onCopyShareLink,
  onContinueWithGithub,
}: {
  data: PublicQuestionResponse;
  meta: PublicWorkspaceMetadata;
  channelName: string;
  messageId: string;
  copied: boolean;
  onCopyShareLink: () => void;
  onContinueWithGithub: () => void;
}) {
  const { question, answers } = data;
  const headline = question.thread_title || question.text;
  return (
    <article className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
          <a
            href={buildAppPath(meta.slug, channelName)}
            className="hover:text-foreground"
          >
            <span className="text-muted-foreground">#</span>
            {data.channel.name}
          </a>
        </div>
        <h1 className="text-2xl font-semibold leading-snug tracking-tight">
          {headline}
        </h1>
        {/* When the headline came from the thread title, keep the original
            question body below it; when it WAS the body, don't repeat it. */}
        {question.thread_title && question.text && (
          <p className="text-base leading-relaxed text-foreground/90">
            {question.text}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {question.sender_name ?? "Someone"} ·{" "}
          {formatRelativeTime(question.created_at)}
        </p>
        <div className="pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onCopyShareLink}
            aria-label="Copy share link"
          >
            {copied ? <Check size={16} /> : <LinkIcon size={16} />}
            {copied ? "Copied" : "Copy share link"}
          </Button>
        </div>
      </div>

      <AnswerList
        answers={answers}
        acceptedAnswerId={question.accepted_answer_id}
      />

      <div className="border-t border-border pt-6">
        {/* An answer is a thread reply (`addReply` derives the channel from the
            parent), but we still pass the real `channel.id` so the post-auth
            hook can gate on the precise channel membership and navigate back to
            the channel after publishing. */}
        <PublicReplyComposer
          slug={meta.slug}
          channelId={data.channel.id}
          channelName={channelName}
          targetMessageId={messageId}
          autoJoin={meta.public_join_policy === "auto"}
          placeholder="Write an answer…"
          onContinueWithGithub={onContinueWithGithub}
        />
      </div>
    </article>
  );
}

/** The answers list — highlights the asker-accepted answer with a badge. */
function AnswerList({
  answers,
  acceptedAnswerId,
}: {
  answers: PublicQuestionResponse["answers"];
  acceptedAnswerId?: string | null;
}) {
  if (answers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No answers yet — be the first to help.
      </p>
    );
  }
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {answers.length === 1 ? "1 answer" : `${answers.length} answers`}
      </h2>
      <ul className="space-y-3">
        {answers.map((a) => {
          const accepted = !!acceptedAnswerId && a.id === acceptedAnswerId;
          return (
            <li
              key={a.id}
              className={
                accepted
                  ? "rounded-lg border border-border bg-foreground/[0.04] p-4"
                  : "rounded-lg border border-border bg-foreground/[0.02] p-4"
              }
            >
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-sm font-medium text-foreground/80">
                  {a.sender_name ?? "Someone"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(a.created_at)}
                </span>
                {accepted && (
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-foreground/10 px-2 py-0.5 text-[11px] font-medium text-foreground">
                    <CheckCircle size={12} />
                    Accepted
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {a.text}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
